import { useState } from 'react'
import { AnimatePresence, motion, useAnimationControls } from 'framer-motion'
import { useTranslation } from 'react-i18next'

import { mutation, query } from '../../graphql/api'
import Typo from '../typo'
import { trackEvent } from '../../utils/analytics'
import { sendSlackMessage } from '../../utils/slack'
import { useMount } from '../../hooks/use-mount'
import { useAnalyseContext } from '../../context/analyse-context'
import { useIsMobile } from '../../hooks/use-is-mobile'

import packageJson from '../../../package.json'
import StarRating from '../star-rating'
import Textarea from '../textarea'
import Button from '../button'
import { isNumber } from 'lodash'
const { version } = packageJson

const Outro = () => {
  const { t } = useTranslation()
  const { state, data, career, partner, invitation } = useAnalyseContext()

  const { title, description, description_2 } = data[state]

  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false)
  const [feedback, setFeedback] = useState('')
  const [rating, setRating] = useState(null)

  const controls = useAnimationControls()

  const { isMobile } = useIsMobile()

  useMount(() => {
    controls.start({ opacity: 1 })

    query({
      query: 'getFeedback',
      variables: { id: invitation },
      authMode: 'AWS_IAM'
    }).then((feedback) => {
      if (feedback !== null) setFeedbackSubmitted(true)
    })
  })

  const partnerName = partner.dispay_name || partner.name

  const onPressSendFeedback = () => {
    const input = {
      id: invitation,
      rating,
      ref_link: window.location.href,
      context: 'AIVY_REQUIREMENTS_ANALYSIS',
      version,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }

    if (feedback) input.rating_text = feedback

    trackEvent('rating_submitted')
    setFeedbackSubmitted(true)

    mutation({
      mutation: 'createFeedback',
      input,
      authMode: 'AWS_IAM'
    }).then(() => {
      const message = `Ein Experte von ${partnerName} hat folgendes Feedback hinterlassen. Rating: ${rating} - Text: ${feedback}`
      sendSlackMessage({ message })
    })
  }

  return (
    <motion.div
      style={{ opacity: 0 }}
      animate={controls}
      transition={{ duration: 1 }}
      className='flex justify-center items-center'
    >
      <div className='m-8 mb-32 md:m-16' style={{ maxWidth: 738 }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <Typo type='h1' translate center>
            {title}
          </Typo>
        </div>
        <div
          style={{
            marginTop: 16,
            padding: isMobile ? undefined : '21px 35px',
            background: isMobile ? undefined : '#EBEBEB',
            borderRadius: 15
          }}
        >
          <Typo type='t1' markdown>
            {t(description, {
              career_title: career.title,
              partner_name: partnerName
            })}
          </Typo>
          <Typo type='t1' markdown style={{ marginTop: 16 }}>
            {t(description_2)}
          </Typo>
        </div>
        <div className='mt-8 md:mt-16 items-center flex flex-col'>
          {!feedbackSubmitted && (
            <>
              <Typo type='h12' center>
                {t('feedback.text')}
              </Typo>
              <div className='mt-4'>
                <StarRating {...{ value: rating, onChange: setRating }} />
              </div>

              <AnimatePresence>
                {isNumber(rating) && rating <= 2 && (
                  <motion.div
                    className='w-full max-w-xl'
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <Textarea
                      id='feedback'
                      label={t('feedback.textarea_label')}
                      value={feedback}
                      className='pt-4'
                      onChange={(event) => setFeedback(event.target.value)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>
              <div className='mt-6' />
              <Button
                type='forward'
                text={t('feedback.send_button')}
                disabled={rating === null}
                onClick={onPressSendFeedback}
              />
            </>
          )}
          {feedbackSubmitted && (
            <Typo type='h12' center>
              {t('feedback.send_success')}
            </Typo>
          )}
        </div>
      </div>
    </motion.div>
  )
}

export default Outro
